import type { VerificationAdditionalCheckStatusEnum, VerificationStateEnum } from '@/enums';
import { emptyFunction, handleApiResponse } from '@/functions';
import type { IVerifications } from '@/lib/interface';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/components/api';
import { storageService } from '@/services';
import type { GenderEnum } from '@/lib/enum';
import routes from '@/lib/routes';
import dayjs from 'dayjs';

type TParams = {
	additionalCheckStatus?: VerificationAdditionalCheckStatusEnum;
  additionalCheckState?: VerificationStateEnum;
  gender?: GenderEnum;
  readonly?: boolean;
};
type TResponseData = IVerifications[];

export const useRequestGetVerificationPhotos = (params?: TParams, run = true) => {

  if (!params?.readonly) {
    delete params?.readonly;
  }

  const { isLoading, isFetching, data, error, dataUpdatedAt: verificationsUpdatedAt, refetch } = useQuery({
    queryKey: [routes.api.verifications.photo, params, run],
    queryFn: ({ queryKey: [_url, _params, _run] }) =>
      !_run
        ? Promise.resolve(null)
        : apiClient
          .get<TResponseData>(_url as string, _params as TParams)
          .then(handleApiResponse)
          .then(data => data?.map(l => ({ ...l, createdAt: dayjs(l.createdAt).toDate() })) ?? [])
          // Remove verifications already done
          .then(
            data => {
              const verifications = storageService.recentVerifications.get();

              if (verifications.length === 0) {
                return data;
              }

              return [...data].filter(item => !verifications.some(l => l.id === item._id));
            }
          )
  });

  const verifications = useMemo(() => data ?? [], [data]);

  return ({
    refreshVerifications: () => refetch().then(emptyFunction),
    verificationsUpdatedAt,
    isLoadingVerifications: isLoading,
    isRefetchingVerifications: isFetching && !isLoading,
    verifications,
    error
  });

};
